import axios from 'axios';
import _ from 'lodash';

import { INTERCOM_APP_ID } from 'constants/envs';
import { getConditionalObject } from 'utils/data';

const messengerState = { bootPromise: null };

export const checkIsIntercomEnabled = () => !!INTERCOM_APP_ID;

// Drive the live window.Intercom rather than the SDK's exported show/hide/shutdown helpers: those go
// through a module-level `ref` that the SDK caches on first use (instance-manager.js) and never
// refreshes. It captures the loader STUB that exists before widget.intercom.io swaps in the real
// function, so every later call lands in a queue nobody drains — the messenger stops reopening, and
// shutdown silently does nothing (leaving the previous user's session live across an identity swap).
const callMessenger = (...args) => {
  if (!_.isFunction(window.Intercom)) {
    return false;
  }

  window.Intercom(...args);

  return true;
};

// Lazy import: this module is statically imported by accountsJwtService (logout shutdown), so a
// static jwtService import here would be a module cycle.
const getAccessToken = async () => {
  const { jwtManager } = await import('app/services/jwtService');

  return jwtManager.getAccessTokenValue();
};

const fetchMessengerIdentity = async () => {
  const accessToken = await getAccessToken();

  if (!accessToken) {
    return null;
  }

  try {
    const { data } = await axios.get('/intercom/user-jwt', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    return data?.jwt ? data : null;
  } catch (error) {
    // Booting as an anonymous visitor keeps support reachable; identity verification simply does
    // not attach for this session.
    console.error('Intercom identity JWT request failed, opening messenger as visitor.', error);

    return null;
  }
};

// Never rejects — a rejected bootPromise would poison every later open/shutdown await. Resolves
// whether the messenger actually booted.
const bootMessenger = async () => {
  try {
    const identity = await fetchMessengerIdentity();
    // User and company attributes travel inside the signed token, never as unsigned settings.
    const settings = {
      app_id: INTERCOM_APP_ID,
      hide_default_launcher: true,
      ...getConditionalObject(!!identity?.jwt, { intercom_user_jwt: identity?.jwt }),
    };

    // An already-installed widget (including one that was shut down) must be re-booted through the
    // global: the SDK's initializer no-ops once its ref is set, so the new identity would be lost.
    if (callMessenger('boot', settings)) {
      return true;
    }

    const { default: Intercom } = await import('@intercom/messenger-js-sdk');

    Intercom(settings);

    return true;
  } catch (error) {
    console.error('Intercom boot failed.', error);

    return false;
  }
};

export const openIntercomMessenger = async () => {
  if (!checkIsIntercomEnabled()) {
    return;
  }

  if (!messengerState.bootPromise) {
    messengerState.bootPromise = bootMessenger();
  }

  const awaitedBoot = messengerState.bootPromise;
  const isBooted = await awaitedBoot;

  // A shutdown (logout / impersonation swap) raced this boot: the session that started it is gone,
  // so tear the freshly-booted messenger back down instead of showing the departed user's session.
  if (messengerState.bootPromise !== awaitedBoot) {
    callMessenger('shutdown');

    return;
  }

  if (!isBooted) {
    // Clear the failed attempt so the next click can retry.
    messengerState.bootPromise = null;

    return;
  }

  callMessenger('show');
};

// Synchronous on purpose: logout and reLogin call this immediately before a hard navigation, so an
// awaited dynamic import would never resolve in time and the session would survive the swap.
export const shutdownIntercomMessenger = () => {
  if (!messengerState.bootPromise) {
    return;
  }

  messengerState.bootPromise = null;
  callMessenger('shutdown');
};

// Submitted from the throwaway tab's own document so the SPA's no-referrer policy (which would send
// `Origin: null`) stays untouched; the server checks Origin. A form POST keeps the token out of URLs.
const submitPortalHandoff = (portalWindow, { url, token }) => {
  const portalDocument = portalWindow.document;
  const referrerMeta = portalDocument.createElement('meta');
  const form = portalDocument.createElement('form');
  const tokenInput = portalDocument.createElement('input');

  referrerMeta.name = 'referrer';
  referrerMeta.content = 'origin';
  portalDocument.head.appendChild(referrerMeta);
  form.method = 'POST';
  form.action = url;
  tokenInput.type = 'hidden';
  tokenInput.name = 'token';
  tokenInput.value = token;
  form.appendChild(tokenInput);
  portalDocument.body.appendChild(form);
  form.submit();
};

// The portal only trusts a messenger cookie on the Help Center's domain, which a white-label host
// can't set, so the server hands the user to an AdminPanel host on that domain first.
export const openIntercomTicketsPortal = async () => {
  if (!checkIsIntercomEnabled()) {
    return;
  }

  // Opened synchronously within the click so popup blockers allow it; the hand-off request is async.
  const portalWindow = window.open('about:blank', '_blank');

  if (!portalWindow) {
    console.error('Intercom tickets portal hand-off failed: the new tab was blocked.');

    return;
  }

  portalWindow.opener = null;

  try {
    const accessToken = await getAccessToken();

    if (!accessToken) {
      throw new Error('No access token.');
    }

    const { data } = await axios.post('/intercom/portal-handoff', null, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    submitPortalHandoff(portalWindow, data);
  } catch (error) {
    portalWindow.close();
    console.error('Intercom tickets portal hand-off failed.', error);
  }
};
